import dayjs from 'dayjs'
import {
  ECampaignChannel,
  ENotificationRecipients,
  ExternalPushMessageData,
  IBroadcastSegments,
  ICampaignChannel,
  INotificationCampaign,
  InternalPushMessageData,
  TCampaignStatus
} from 'models/notifications'
import { getNotificationTypeName } from './getNotificationTypeName'

// Как на бэкенде: письма уходят по 5 штук с паузой 3 с — иначе mail.ru режет отправку
const EMAIL_BATCH_SIZE = 5
const EMAIL_BATCH_PAUSE_SEC = 3

export const TITLE_MAX_LENGTH = 200
export const BODY_MAX_LENGTH = 5000
// Как на бэкенде: пять SMS кириллицей
export const SMS_MAX_LENGTH = 335
export const POPUP_BUTTON_MAX_LENGTH = 40
export const NO_ORDERS_DAYS_MAX = 3650

export const channelNames: Record<ECampaignChannel, string> = {
  [ECampaignChannel.PUSH]: 'Push-уведомление',
  [ECampaignChannel.EMAIL]: 'E-mail рассылка',
  [ECampaignChannel.BOT]: 'Seller-бот',
  [ECampaignChannel.SMS]: 'SMS-рассылка',
  [ECampaignChannel.POPUP]: 'Всплывающее окно'
}

export const recipientNames: Record<ENotificationRecipients, string> = {
  [ENotificationRecipients.ALL]: 'Продавцам и пользователям',
  [ENotificationRecipients.SELLERS]: 'Продавцам',
  [ENotificationRecipients.USERS]: 'Пользователям'
}

export const statusNames: Record<TCampaignStatus, string> = {
  scheduled: 'Запланирована',
  queued: 'В очереди',
  running: 'Идёт',
  completed: 'Завершена',
  failed: 'С ошибкой',
  canceled: 'Отменена'
}

const FINISHED_STATUSES: TCampaignStatus[] = ['completed', 'failed', 'canceled']

export const isCampaignActive = (status?: TCampaignStatus) =>
  status === 'queued' || status === 'running'

export const canCancelCampaign = (status?: TCampaignStatus) =>
  status === 'scheduled' || isCampaignActive(status)

// Рассылка «спит» до утра, если каждый неотправленный канал ждёт 9:00 у продавцов
export const getMorningWait = (campaign: INotificationCampaign) => {
  if (!isCampaignActive(campaign.status)) return null
  const pending = (campaign.channels ?? []).filter(
    (channel) => !FINISHED_STATUSES.includes(channel.status)
  )
  const now = Date.now()
  if (
    !pending.length ||
    pending.some(
      (channel) => !channel.next_at || dayjs(channel.next_at).valueOf() <= now
    )
  ) {
    return null
  }
  return pending.reduce<string>(
    (earliest, channel) =>
      dayjs(channel.next_at).isBefore(earliest) ? channel.next_at! : earliest,
    pending[0].next_at!
  )
}

// Старые рассылки шли одним каналом без разбивки — показываем их как один канал
export const getCampaignChannels = (
  campaign: INotificationCampaign
): ICampaignChannel[] =>
  campaign.channels?.length
    ? campaign.channels
    : [
        {
          key: campaign.channel ?? ECampaignChannel.PUSH,
          total: campaign.total,
          sent: campaign.sent,
          failed: campaign.failed,
          status: campaign.status,
          error: campaign.error ?? null
        }
      ]

export const formatDateTime = (value: string | Date) =>
  dayjs(value).format('DD.MM.YYYY HH:mm')

export const formatRub = (value: number) =>
  `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`

export const getAdminName = (
  admin?: { name?: string; surname?: string; email?: string } | null
) =>
  (admin &&
    ([admin.name, admin.surname].filter(Boolean).join(' ') || admin.email)) ||
  '—'

export const getLinkLabel = (
  data?: InternalPushMessageData | ExternalPushMessageData | null
) => {
  if (!data) return null
  if ('url' in data && data.url) return data.url
  if ('type' in data && data.type) {
    const { type, typeSlug } = data as InternalPushMessageData
    return `${getNotificationTypeName(type)}${typeSlug ? ` · ${typeSlug}` : ''}`
  }
  return null
}

export const getSegmentLines = (segments?: IBroadcastSegments | null) =>
  [
    segments?.in_cart && 'с товарами в корзине',
    segments?.registered_after &&
      `зарегистрированы с ${dayjs(segments.registered_after).format(
        'DD.MM.YYYY'
      )}`,
    segments?.no_orders_days &&
      `без оплаченных заказов ${plural(segments.no_orders_days, [
        'день',
        'дня',
        'дней'
      ])}`
  ].filter((line): line is string => Boolean(line))

export const plural = (count: number, forms: [string, string, string]) => {
  const mod10 = count % 10
  const mod100 = count % 100
  const form =
    mod10 === 1 && mod100 !== 11
      ? forms[0]
      : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
      ? forms[1]
      : forms[2]
  return `${count.toLocaleString('ru-RU')} ${form}`
}

export const devices = (count: number) =>
  plural(count, ['устройство', 'устройства', 'устройств'])
export const addresses = (count: number) =>
  plural(count, ['адрес', 'адреса', 'адресов'])
export const sellersCount = (count: number) =>
  plural(count, ['продавец', 'продавца', 'продавцов'])
// «у 1 продавца», «у 5 продавцов»
export const sellersGenitive = (count: number) =>
  plural(count, ['продавца', 'продавцов', 'продавцов'])
export const phonesCount = (count: number) =>
  plural(count, ['номер', 'номера', 'номеров'])
export const chatsCount = (count: number) =>
  plural(count, ['чат', 'чата', 'чатов'])
export const smsCount = (count: number) => plural(count, ['SMS', 'SMS', 'SMS'])

export const channelUnits: Record<ECampaignChannel, (count: number) => string> =
  {
    [ECampaignChannel.PUSH]: devices,
    [ECampaignChannel.EMAIL]: addresses,
    [ECampaignChannel.BOT]: chatsCount,
    [ECampaignChannel.SMS]: phonesCount,
    [ECampaignChannel.POPUP]: sellersCount
  }

// Латиница помещается в SMS по 160 символов, любая кириллица — по 70; длинное
// сообщение режется на части по 153 и 67 — часть места уходит на склейку
export const getSmsSegments = (text: string) => {
  if (!text) return 0
  const isLatin = /^[\x20-\x7E\n\r]*$/.test(text)
  const single = isLatin ? 160 : 70
  const multi = isLatin ? 153 : 67
  return text.length <= single ? 1 : Math.ceil(text.length / multi)
}

// Нижняя оценка: только паузы между пачками, без времени самой отправки
export const getEmailDuration = (emails: number) => {
  const seconds =
    Math.max(Math.ceil(emails / EMAIL_BATCH_SIZE) - 1, 0) *
    EMAIL_BATCH_PAUSE_SEC
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 2) return 'около минуты'
  if (minutes < 60) return `не меньше ${minutes} мин`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return `не меньше ${hours} ч${rest ? ` ${rest} мин` : ''}`
}
